import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import {
  clearAuthCookies,
  getJwtFromCookie,
  getRefreshTokenFromCookie,
  setAuthCookies,
} from '../cookies';
import { getApiUrl } from '../runtime-config';
import { LoginResponse } from './api/auth/auth.interface';

type RetryConfig = InternalAxiosRequestConfig & { _retry?: boolean };

export const axiosInstance = axios.create({
  baseURL: getApiUrl(),
  withCredentials: true,
});

let refreshPromise: Promise<string> | null = null;

export async function refreshAccessToken(): Promise<string> {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    const refreshToken = getRefreshTokenFromCookie();
    if (!refreshToken) {
      throw new Error('NO_REFRESH_TOKEN');
    }

    const { data } = await axios.post<LoginResponse>(
      `${getApiUrl()}/api/auth/refresh-token`,
      { refreshToken },
      { withCredentials: true },
    );

    setAuthCookies(data.accessToken, data.refreshToken);
    return data.accessToken;
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}

axiosInstance.interceptors.request.use((config) => {
  const token = getJwtFromCookie();

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryConfig | undefined;

    if (
      !originalRequest ||
      error.response?.status !== 401 ||
      originalRequest._retry
    ) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      const accessToken = await refreshAccessToken();
      originalRequest.headers.Authorization = `Bearer ${accessToken}`;
      return axiosInstance(originalRequest);
    } catch {
      clearAuthCookies();
      return Promise.reject(error);
    }
  },
);
